import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { Filter, RotateCcw } from "../icons/icons";
import { cx } from "../lib/cx";
import { useUiStrings } from "../strings/ui-strings";

/**
 * FilterBar — the filter panel that sits ABOVE a DataTable, never inside it.
 *
 * Platform rule: nothing filters on a keystroke. Fields edit a local draft;
 * the "Filter" button applies it (the app then writes the values to the URL
 * so a copied link shows exactly what was chosen), and "Reset" clears both.
 * The bar is a <form>, so Enter in any text field applies too.
 */
export interface FilterBarProps {
	children: ReactNode;
	onApply: () => void;
	onReset: () => void;
	/** Disables the buttons and shows a spinner on Filter. */
	busy?: boolean;
	/** Highlights the Reset button and shows the count: "Reset (2)". */
	activeCount?: number;
	/** Override the provider's "Filter" / "Reset" for this bar. */
	applyLabel?: ReactNode;
	resetLabel?: ReactNode;
	/** Accessible name of the form. Default from the provider ("Filters"). */
	"aria-label"?: string;
	/** Extra controls on the right (e.g. "Export"). */
	end?: ReactNode;
	/** Grid column template. Default: auto-fill 14rem columns. */
	columns?: string;
	className?: string;
}

export function FilterBar({
	children,
	onApply,
	onReset,
	busy = false,
	activeCount = 0,
	applyLabel,
	resetLabel,
	end,
	columns = "repeat(auto-fill, minmax(min(14rem, 100%), 1fr))",
	className,
	"aria-label": ariaLabel,
}: FilterBarProps) {
	const { filterBar: strings, formatNumber } = useUiStrings();
	const submit = (e: FormEvent) => {
		e.preventDefault();
		onApply();
	};
	return (
		<form
			onSubmit={submit}
			onReset={(e) => {
				e.preventDefault();
				onReset();
			}}
			className={cx(
				// Her dashboard filter card (`.dfb`): white, a --line border, 10px radius, 16px in.
				"flex flex-col gap-[0.75rem] rounded-box border border-gms-line bg-base-100 p-[1rem]",
				className,
			)}
			aria-label={ariaLabel ?? strings.label}
		>
			<div className="grid gap-x-[0.75rem] gap-y-[0.625rem]" style={{ gridTemplateColumns: columns }}>
				{children}
			</div>
			<div className="flex flex-wrap items-center gap-[0.5rem] border-t border-gms-line pt-[0.75rem]">
				<button type="submit" className="btn btn-primary btn-sm gap-[0.375rem]" disabled={busy}>
					{busy ? <span className="loading loading-spinner loading-xs" /> : <Filter className="size-[0.875rem]" />}
					{applyLabel ?? strings.apply}
				</button>
				<button
					type="reset"
					// Her `.btn--line` (white, --line-2 border), with the active count in her `.fchip__n` red dot (daisyUI accent).
					className="btn btn-ghost btn-sm gap-[0.375rem] border-gms-line-2 bg-base-100 text-gms-navy-ink hover:border-gms-navy hover:bg-base-100 [&>svg]:text-gms-ink-2"
					disabled={busy}
				>
					<RotateCcw className="size-[0.875rem]" />
					{resetLabel ?? strings.reset}
					{activeCount > 0 && (
						<span className="grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full bg-accent px-[0.3125rem] text-[0.6875rem] font-bold text-accent-content tabular-nums">
							{formatNumber(activeCount)}
						</span>
					)}
				</button>
				{end && <div className="ml-auto flex items-center gap-[0.5rem]">{end}</div>}
			</div>
		</form>
	);
}

/** Label + control, for use inside FilterBar's grid. */
export function FilterField({
	label,
	htmlFor,
	children,
	className,
	span,
}: {
	label: ReactNode;
	htmlFor?: string;
	children: ReactNode;
	className?: string;
	/**
	 * `"full"` stretches across every column (a wide search box). A number
	 * spans N columns but never more than exist — `span N` on an auto-fill grid
	 * that has collapsed to one column would otherwise conjure an implicit
	 * overflowing column (the iPhone SE bug).
	 */
	span?: number | "full";
}) {
	const gridColumn = span === "full" ? "1 / -1" : span ? `span ${span}` : undefined;
	return (
		<div
			className={cx("flex min-w-0 flex-col gap-[0.375rem]", className)}
			style={gridColumn ? { gridColumn, maxWidth: "100%" } : undefined}
		>
			<label htmlFor={htmlFor} className="text-[0.84375rem] leading-[1.3] font-medium text-gms-navy-ink">
				{label}
			</label>
			{children}
		</div>
	);
}

/**
 * Draft state for a FilterBar. `applied` is the source of truth (usually the
 * URL search params); the draft re-syncs whenever it changes (back button,
 * "Clear filters" elsewhere), and `set` edits the draft without touching the
 * URL until `apply()`.
 *
 *   const f = useFilterDraft(appliedFromUrl, (next) => navigate({ search: next }));
 *   <FilterBar onApply={f.apply} onReset={f.reset} activeCount={f.activeCount}>
 *     <Select value={f.draft.role} onChange={(v) => f.set("role", v)} … />
 */
export function useFilterDraft<T extends Record<string, unknown>>(
	applied: T,
	onApply: (next: T) => void,
	empty: T,
) {
	const [draft, setDraft] = useState<T>(applied);
	const appliedKey = JSON.stringify(applied);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => setDraft(applied), [appliedKey]);

	const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
		setDraft((d) => ({ ...d, [key]: value }));
	}, []);
	const apply = useCallback(() => onApply(draft), [draft, onApply]);
	const reset = useCallback(() => {
		setDraft(empty);
		onApply(empty);
	}, [empty, onApply]);

	const activeCount = Object.entries(applied).filter(([k, v]) => {
		if (v === undefined || v === null || v === "") return false;
		if (Array.isArray(v) && v.length === 0) return false;
		return JSON.stringify(v) !== JSON.stringify(empty[k]);
	}).length;

	const dirty = JSON.stringify(draft) !== appliedKey;

	return { draft, set, apply, reset, activeCount, dirty };
}
